import { useCallback, useMemo, useState } from "react";
import { UserProfileCard } from "@its-engineering-and-development/ui-foundation/components";
import { useAppSelector } from "@app/store";
import { selectAccessToken } from "@app/store/auth.slice";
import { useLogout } from "@app/hooks/use-logout";
import { LoadingState, LOADING_STATE_VARIANT } from "@app/components/base/loading-state/loading-state";
import { authClient } from "@/api/auth-client";

function IconMenu() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
        </svg>
    );
}

export interface AppHeaderProps {
    onOpenMobileMenu: () => void;
}

export function AppHeader({ onOpenMobileMenu }: AppHeaderProps) {
    const accessToken = useAppSelector(selectAccessToken);
    // Re-decode when the token changes so the card refreshes on login/logout.
    const decoded = useMemo(() => authClient.decodeToken(), [accessToken]);
    const logout = useLogout();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleLogout = useCallback(async () => {
        setIsLoggingOut(true);
        await logout();
    }, [logout]);

    const menuItems = useMemo(
        () => [
            {
                id: "logout",
                label: "Log out",
                // Swallow rejection; `finally` has already cleared state and redirected.
                onClick: () => void handleLogout().catch(() => undefined),
            },
        ],
        [handleLogout],
    );

    return (
        <>
            {isLoggingOut && <LoadingState variant={LOADING_STATE_VARIANT.Fullscreen} label="Logging out" />}
            <header className="flex h-14 items-center justify-between border-b border-gray-200 bg-white px-3 sm:px-6 md:justify-end">
                <button type="button" aria-label="Open menu" className="rounded p-2 text-gray-600 hover:bg-gray-100 md:hidden" onClick={onOpenMobileMenu}>
                    <IconMenu />
                </button>

                <UserProfileCard
                    name={decoded?.name ?? ""}
                    email={decoded?.email ?? ""}
                    role={decoded?.roles?.[0] ?? null}
                    menuItems={menuItems}
                    nameClassName="text-right"
                    emailClassName="text-right"
                    roleClassName="text-right"
                    className="py-2 pr-3 !outline-none [&_[class*='animate-pulse']]:!bg-gray-200 [&_p]:text-gray-900"
                />
            </header>
        </>
    );
}
